import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "theme-vs-storefront"
    }}>{`Theme vs Storefront`}</h1>
    <p>{`An Adobe Commerce or Magento Open Source store uses theme components to define the look and feel of the front-end store.
PWA Studio introduces a different approach for creating a custom front-end experience using storefront applications.
This topic compares the traditional theme development approach of Adobe Commerce and Magento Open Source with a more modern approach of developing a progressive storefront using PWA Studio tools.`}</p>
    <h2 {...{
      "id": "definitions"
    }}>{`Definitions`}</h2>
    <h3 {...{
      "id": "adobe-commerce-and-magento-open-source-theme"
    }}>{`Adobe Commerce and Magento Open Source theme`}</h3>
    <p>{`An Adobe Commerce or Magento Open Source theme is a type of `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/php/development/"
      }}>{`component`}</a>{` that defines how a store looks.
It is deeply integrated with the application and depends on the core code for functionality.`}</p>
    <p>{`A theme is always built on top of an existing parent theme.
Out of the box, the Adobe Commerce and Magento Open Source applications provide the `}<strong parentName="p">{`Blank`}</strong>{` and `}<strong parentName="p">{`Luma`}</strong>{` themes that developers extend or customize to create custom storefronts.`}</p>
    <p>{`All themes inherit from a parent theme.
At the top of the inheritance chain is the Blank theme, which provides the base files for a theme.
Every other theme builds on, overrides, or customizes these files.`}</p>
    <p>{`For more information on themes, see the `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/frontend-core/guide/"
      }}>{`Frontend Developer Guide`}</a>{` for the core application.`}</p>
    <h3 {...{
      "id": "pwa-studio-storefront-application"
    }}>{`PWA Studio storefront application`}</h3>
    <p>{`A PWA Studio storefront application is a `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/"
      }}>{`progressive web app`}</a>{` built using PWA Studio tools.`}</p>
    <p>{`It is made up of an application shell that exists in the browser and a `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/packages/upward/"
      }}>{`middle tier service layer`}</a>{` that sits between the shell and a `}<a parentName="p" {...{
        "href": "https://magento.com/blog/best-practices/future-headless"
      }}>{`headless backend`}</a>{` running Adobe Commerce or Magento Open Source.`}</p>
    <p>{`Unlike an Adobe Commerce or Magento Open Source theme, a storefront application does not inherit from a parent theme.
Instead, the application is composed of `}<a parentName="p" {...{
        "href": "https://reactjs.org/"
      }}>{`React`}</a>{` modules that provide the different pieces of functionality.
These pieces can be swapped out to change behavior or even removed entirely.`}</p>
    <p>{`For an example of a storefront application, read about the `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/packages/venia/"
      }}>{`Venia`}</a>{` storefront, a reference implementation created using PWA Studio tools.`}</p>
    <h3 {...{
      "id": "components"
    }}>{`Components`}</h3>
    <p>{`Both theme and storefront applications use pieces that are known as `}<strong parentName="p">{`components`}</strong>{`, but
each approach uses a different definition of this term.`}</p>
    <p>{`In theme development, `}<em parentName="p">{`components`}</em>{` mostly refers to `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/frontend-core/ui-components/"
      }}>{`Adobe's UI Components`}</a>{`.
These components are standard UI elements, such as tables, buttons, and dialogs, that Adobe provides to make theme development easier.`}</p>
    <p>{`In PWA Studio, `}<em parentName="p">{`components`}</em>{` refers to `}<a parentName="p" {...{
        "href": "https://reactjs.org/"
      }}>{`React`}</a>{` components.
React components are modular pieces that make up a React application, such as a PWA storefront.
These pieces can contain pure logic, such as `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/packages/peregrine/"
      }}>{`Peregrine components`}</a>{`, or visual elements, such as `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/packages/venia/ui-components/"
      }}>{`Venia's UI components`}</a>{`.`}</p>
    <h2 {...{
      "id": "dependency-management"
    }}>{`Dependency management`}</h2>
    <p>{`A theme uses a `}<a parentName="p" {...{
        "href": "https://getcomposer.org/doc/00-intro.md"
      }}>{`Composer`}</a>{` file to specify its dependencies and relies on the application's file resolution mechanism to make sure the required theme files are available.`}</p>
    <p>{`PWA Studio uses `}<a parentName="p" {...{
        "href": "https://yarnpkg.com/"
      }}>{`Yarn`}</a>{` for dependency management and validation scripts to make sure it is compatible with the backend application server.`}</p>
    <h2 {...{
      "id": "inheritance-vs-modularity"
    }}>{`Inheritance vs modularity`}</h2>
    <p>{`Since themes inherit from a parent theme, the effort needed to create a custom storefront is dependent on the additional customizations needed on top of a base theme.
Some themes build off `}<strong parentName="p">{`Luma`}</strong>{` or other existing themes and apply minimal customizations, such as logo and color changes, to create a unique look for a store.
Other themes use `}<strong parentName="p">{`Blank`}</strong>{` as the base and require more customization work.`}</p>
    <p>{`This inheritance approach tightly couples a theme with the parent themes up its inheritance chain.
As a result, parent theme updates can lead to unexpected side effects for its dependent child themes.`}</p>
    <p>{`PWA storefront development uses a more modular approach for creating a storefront.`}</p>
    <p>{`Instead of starting with a base theme and customizing the pieces, a developer puts together a storefront from scratch using different React modules.
This approach provides greater flexibility and control during the storefront creation process.`}</p>
    <p>{`Since they are independent and modular, side effects from updating each piece is minimized.
Non-breaking updates can be applied to other modules while keeping others to a stable version.`}</p>
    <h2 {...{
      "id": "required-skillset"
    }}>{`Required skillset`}</h2>
    <p>{`The skillset required to work with themes is different from the skillset required for a PWA storefront because of the technologies used in each approach.`}</p>
    <h3 {...{
      "id": "adobe-commerce-and-magento-open-source-frontend-developers"
    }}>{`Adobe Commerce and Magento Open Source frontend developers`}</h3>
    <p>{`In addition to knowing standard JS libraries, such as jQuery and KnockoutJS, theme developers for Adobe Commerce and Magento Open Source need general knowledge about the application's components and specialized knowledge about its templating and layout system.`}</p>
    <p>{`The following table is a summary of general skills needed for theme development:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Skill`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PHP`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The primary language of the Adobe Commerce and Magento Open Source`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`JavaScript (JS)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Core web language`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://jquery.com/"
            }}>{`jQuery`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A JS library used for things such as DOM manipulations`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://knockoutjs.com/"
            }}>{`KnockoutJS`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`JS framework used for binding data models to the UI`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Cascading Style Sheets (`}<a parentName="td" {...{
              "href": "https://developer.adobe.com/commerce/frontend-core/guide/css/"
            }}>{`CSS`}</a>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Defines the style for a websites`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Leaner Style Sheets (`}<a parentName="td" {...{
              "href": "http://lesscss.org/"
            }}>{`Less`}</a>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Language extension for CSS`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`XML`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Format used by Adobe Commerce and Magento Open Source for configuration and layout`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.adobe.com/commerce/frontend-core/guide/layouts/"
            }}>{`Theme layouts`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Layouts represent the structure for a page`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.adobe.com/commerce/frontend-core/guide/templates/"
            }}>{`Theme templates`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Templates define how layout blocks are presented on a page`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://commerce-docs.github.io/magento2-ui-library/"
            }}>{`Theme UI library`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Frontend library for theme developers`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.adobe.com/commerce/frontend-core/ui-components/"
            }}>{`Theme UI components`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Another frontend library for theme developers`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://getcomposer.org/doc/00-intro.md"
            }}>{`Composer`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Package and dependency manager`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "pwa-storefront-developers"
    }}>{`PWA storefront developers`}</h3>
    <p>{`The barrier for entry for a PWA Studio developer is lower than a theme developer.
PWA Studio development requires less specialized knowledge to create a custom storefront, and
the tools and concepts it uses are more common and standard among the general front-end developer community.`}</p>
    <p>{`The following table is a summary of general skills needed for PWA storefront development:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Skill`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`JavaScript (JS)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Core web language`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://reactjs.org/"
            }}>{`React`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A JS library for building user interfaces`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://redux.js.org/"
            }}>{`Redux`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A JS library for handling application state`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/commerce/pwa-studio/guides/general-concepts/graphql/"
            }}>{`GraphQL`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An API query language`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://webpack.js.org/"
            }}>{`webpack`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Project assets bundler`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Cascading Style Sheets (`}<a parentName="td" {...{
              "href": "https://developer.adobe.com/commerce/frontend-core/guide/css/"
            }}>{`CSS`}</a>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Defines the style for a websites`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/commerce/pwa-studio/guides/general-concepts/css-modules/"
            }}>{`CSS modules`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Locally scoped style definitions`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`JavaScript Object Notation (`}<a parentName="td" {...{
              "href": "https://www.json.org/"
            }}>{`JSON`}</a>{`)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Data-interchange format`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/commerce/pwa-studio/guides/packages/peregrine/"
            }}>{`Peregrine`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`React components library provided by the PWA Studio`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://yarnpkg.com/"
            }}>{`Yarn`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`JS package and dependency manager`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      